/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import cn from "classnames";

import { memo, ReactNode, RefObject, useEffect, useRef, useState } from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { UseMediaStreamResult } from "../../hooks/use-media-stream-mux";

import { useWebcam } from "../../hooks/use-webcam";
import { AudioRecorder } from "../../lib/audio-recorder";
// import AudioPulse from "../audio-pulse/AudioPulse";
import "./control-tray.scss";
import { VOLUME_SPEAKING_THRESHOLD, THINKING_TRIGGER_DELAY_MS } from "../../lib/constants";

export type ControlTrayProps = {
    videoRef: RefObject<HTMLVideoElement>;
    children?: ReactNode;
    supportsVideo: boolean;
    onVideoStreamChange?: (stream: MediaStream | null) => void;
    enableEditingSettings?: boolean;
    onGenerateReport?: () => void;
};

type MediaStreamButtonProps = {
    isStreaming: boolean;
    onIcon: string;
    offIcon: string;
    start: () => Promise<any>;
    stop: () => any;
};

const MediaStreamButton = memo(
    ({ isStreaming, onIcon, offIcon, start, stop }: MediaStreamButtonProps) =>
        isStreaming ? (
            <button className="action-button" onClick={stop}>
                <span className="material-symbols-outlined">{onIcon}</span>
            </button>
        ) : (
            <button className="action-button" onClick={start}>
                <span className="material-symbols-outlined">{offIcon}</span>
            </button>
        )
);

function ControlTray({
    videoRef,
    children,
    onVideoStreamChange = () => { },
    supportsVideo,
    enableEditingSettings,
    onGenerateReport,
}: ControlTrayProps) {
    const videoStreams = [useWebcam()];
    const [webcam] = videoStreams;
    const [inVolume, setInVolume] = useState(0);
    const [audioRecorder] = useState(() => new AudioRecorder());
    const [muted, setMuted] = useState(false);
    const connectButtonRef = useRef<HTMLButtonElement>(null);

    const { client, connected, connect, /* volume, */ setUserVolume, setIsThinking, isAiSpeaking } =
        useLiveAPIContext();

    const lastSpeakingRef = useRef<number>(0);
    const thinkingTriggerRef = useRef<number | null>(null);
    const isAiSpeakingRef = useRef(isAiSpeaking);
    const hasSpokenRef = useRef(false);
    const lastVolumeRef = useRef(0);

    useEffect(() => {
        isAiSpeakingRef.current = isAiSpeaking;
        if (isAiSpeaking && thinkingTriggerRef.current) {
            clearTimeout(thinkingTriggerRef.current);
            thinkingTriggerRef.current = null;
            setIsThinking(false);
        }
        if (!isAiSpeaking) {
            hasSpokenRef.current = false;
            lastSpeakingRef.current = 0;
        }
    }, [isAiSpeaking, setIsThinking]);

    useEffect(() => {
        if (!connected) {
            lastSpeakingRef.current = 0;
            hasSpokenRef.current = false;
            setIsThinking(false);
            if (thinkingTriggerRef.current) {
                clearTimeout(thinkingTriggerRef.current);
                thinkingTriggerRef.current = null;
            }
        }
    }, [connected, setIsThinking]);

    useEffect(() => {
        if (!connected && connectButtonRef.current) {
            connectButtonRef.current.focus();
        }
    }, [connected]);

    // auto-start webcam (skip on mobile / when video not supported)
    useEffect(() => {
        if (!supportsVideo) return;
        async function initWebcam() {
            try {
                const stream = await webcam.start();
                onVideoStreamChange(stream);
            } catch (e) {
                console.error("Failed to auto-start webcam:", e);
            }
        }
        initWebcam();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supportsVideo]);

    // Volume CSS variable update with threshold
    useEffect(() => {
        const newVal = Math.max(5, Math.min(inVolume * 200, 8));
        if (Math.abs(newVal - lastVolumeRef.current) > 0.5) {
            lastVolumeRef.current = newVal;
            document.documentElement.style.setProperty("--volume", `${newVal}px`);
        }
    }, [inVolume]);

    useEffect(() => {
        const onData = (base64: string) => {
            client.sendRealtimeInput([
                {
                    mimeType: "audio/pcm;rate=16000",
                    data: base64,
                },
            ]);
        };

        const onVolume = (v: number) => {
            setInVolume(v);
            setUserVolume(v);

            if (v > VOLUME_SPEAKING_THRESHOLD) {
                lastSpeakingRef.current = Date.now();
                hasSpokenRef.current = true;
                if (thinkingTriggerRef.current) {
                    clearTimeout(thinkingTriggerRef.current);
                    thinkingTriggerRef.current = null;
                }
                setIsThinking(false);
            } else if (hasSpokenRef.current && lastSpeakingRef.current > 0 && !isAiSpeakingRef.current) {
                if (!thinkingTriggerRef.current) {
                    thinkingTriggerRef.current = window.setTimeout(() => {
                        if (!isAiSpeakingRef.current && hasSpokenRef.current) {
                            setIsThinking(true);
                        }
                        thinkingTriggerRef.current = null;
                    }, THINKING_TRIGGER_DELAY_MS);
                }
            }
        };

        if (connected && !muted && audioRecorder) {
            audioRecorder
                .on("data", onData)
                .on("volume", onVolume)
                .start();
        } else {
            audioRecorder.stop();
            setUserVolume(0);
        }
        return () => {
            audioRecorder
                .off("data", onData)
                .off("volume", onVolume);
        };
    }, [connected, client, muted, audioRecorder, setUserVolume, setIsThinking]);


    const changeStreams = (next?: UseMediaStreamResult) => async () => {
        if (next) {
            const mediaStream = await next.start();
            onVideoStreamChange(mediaStream);
        } else {
            onVideoStreamChange(null);
        }

        videoStreams.filter((msr) => msr !== next).forEach((msr) => msr.stop());
    };

    return (
        <section className="control-tray">
            <nav className="actions-nav">
                <button
                    className={cn("action-button mic-button")}
                    onClick={() => setMuted(!muted)}
                >
                    {!muted ? (
                        <span className="material-symbols-outlined filled">mic</span>
                    ) : (
                        <span className="material-symbols-outlined filled">mic_off</span>
                    )}
                </button>

                {/* <div className="action-button no-action outlined">
                    <AudioPulse volume={volume} active={connected} hover={false} />
                </div> */}

                {supportsVideo && (
                    <>
                        <MediaStreamButton
                            isStreaming={webcam.isStreaming}
                            start={changeStreams(webcam)}
                            stop={changeStreams()}
                            onIcon="videocam"
                            offIcon="videocam_off"
                        />
                    </>
                )}
                {children}
            </nav>

            <div className={cn("connection-container", { connected })}>
                <div className="connection-button-container">
                    {connected ? (
                        <button
                            className="action-button end-meeting-button"
                            onClick={onGenerateReport}
                            title="Toplantıyı Bitir ve Rapor Al"
                        >
                            <span className="material-symbols-outlined filled">call_end</span>
                        </button>
                    ) : (
                        <button
                            ref={connectButtonRef}
                            className={cn("action-button connect-toggle", { connected })}
                            onClick={connect}
                        >
                            <span className="material-symbols-outlined filled">play_arrow</span>
                        </button>
                    )}
                </div>
            </div>
        </section>
    );
}

export default memo(ControlTray);
